import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { LOGOS } from "@/data/site";
import CPMark from "@/components/CPMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "I'm not sure where to start",
  "What services do you offer?",
  "How do I schedule a consultation?",
];

// Turn bare internal paths (/assessment, /contact, /services/xxx) and http(s) URLs
// and emails/phones into clickable links. Runs on each assistant chunk.
// IMPORTANT: single capturing group only, `String.split()` with multiple groups
// duplicates matches in the resulting array.
const URL_RE = /(https?:\/\/[^\s)]+|\/(?:assessment|contact|team|services|industries|resources|blog|careers|about)(?:\/[a-z0-9-]+)?\b|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}|\(\d{3}\)\s?\d{3}-\d{4})/g;
// Strip surrounding markdown bold/italic wrappers the LLM sometimes emits around links.
const stripMd = (s) => s.replace(/\*{1,2}|_{1,2}/g, "");
function renderRichText(text) {
  if (!text) return null;
  // Remove markdown emphasis characters so **/assessment** doesn't render as "**/assessment**".
  const clean = stripMd(text);
  const parts = clean.split(URL_RE);
  return parts.map((p, i) => {
    if (!p) return null;
    if (i % 2 === 1) {
      if (p.startsWith("http")) {
        return <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="text-gold underline underline-offset-2 hover:text-navy">{p}</a>;
      }
      if (p.startsWith("/")) {
        return <a key={i} href={p} className="text-gold underline underline-offset-2 hover:text-navy font-600">{p}</a>;
      }
      if (p.includes("@")) {
        return <a key={i} href={`mailto:${p}`} className="text-gold underline underline-offset-2 hover:text-navy">{p}</a>;
      }
      if (p.startsWith("(")) {
        return <a key={i} href={`tel:${p.replace(/[^0-9+]/g, "")}`} className="text-gold underline underline-offset-2 hover:text-navy">{p}</a>;
      }
    }
    return <span key={i}>{p}</span>;
  });
}

export default function AIChat() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Welcome to Consilium Pro. I'm your executive AI assistant, ask me about our services, industries we serve, or how to schedule a consultation." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("Bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I'm sorry, I couldn't connect. Please reach us at info@consiliumpro.co or (678) 572-1813." };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen(!open)}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 200 }}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 bg-navy text-white flex items-center justify-center shadow-[0_12px_40px_rgba(15,45,82,0.4)] hover:bg-gold transition-colors duration-300"
        aria-label="Open AI assistant"
      >
        {open ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" strokeWidth={1.5} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-chat-panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[calc(100vh-8rem)] flex flex-col backdrop-blur-xl bg-white/95 border border-slate-200 shadow-[0_24px_80px_rgba(15,45,82,0.25)]"
          >
            <div className="bg-navy text-white px-5 py-4 flex items-center gap-3">
              <CPMark className="w-16 h-16" variant="dark" />
              <div>
                <p className="font-display font-700 text-sm leading-tight">Consilium Pro Assistant</p>
                <p className="text-white/60 text-xs flex items-center gap-1"><Sparkles className="w-3 h-3 text-gold" /> AI-powered · GPT-5.2</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user" ? "bg-navy text-white" : "bg-lightgray text-navy border border-slate-200"
                    }`}
                  >
                    {m.content ? renderRichText(m.content) : <span className="inline-flex gap-1"><span className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse" /><span className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse [animation-delay:150ms]" /><span className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse [animation-delay:300ms]" /></span>}
                  </div>
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="space-y-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid="ai-chat-suggestion" onClick={() => send(s)} className="block w-full text-left text-sm px-4 py-2.5 border border-slate-200 text-navy hover:border-gold hover:text-gold transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-slate-200 p-3 flex items-center gap-2"
            >
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about our services…"
                className="flex-1 bg-lightgray px-4 py-3 text-sm text-navy outline-none focus:ring-2 focus:ring-gold"
              />
              <button data-testid="ai-chat-send" type="submit" disabled={loading} className="bg-navy text-white w-11 h-11 flex items-center justify-center hover:bg-gold transition-colors disabled:opacity-50">
                <Send className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
